import React from 'react';
import PropTypes from 'prop-types';
import { Collapse, Icon, List } from '@pella/sash-framework';
import { CTA } from '../../../';
import { isValidArray } from '../../../utilities/validations';

import Tile from '../../header/navigation/menu-types/product/tile';
import BackButton from '../back-button';

const ProductMenu = ({ data, onMenuItemClick, parent, children }) => {
	if (!isValidArray(data?.category)) return null;

	return (
		<List
			bullets='none'
			className='mobileProductMenu invisible absolute -right-80 top-0 z-[11] h-[calc(100svh-59px)] w-full overflow-hidden bg-gray-3 shadow-xl motion-safe:transition-all motion-safe:duration-500 sm:h-[calc(100svh-63px)]'
		>
			{children}

			{data.category
				.filter((cat) => ['online', 'by appointment', 'parts'].includes(cat.category_type))
				.map((category, index) => (
					<li key={index}>
						<Collapse
							className='!border-0 bg-white'
							isOpen
						>
							<Collapse.Trigger className='!px-4 !py-2 text-sm font-semibold'>{category.heading}</Collapse.Trigger>
							<Collapse.Content className='space-y-2 bg-gray-3 !px-4 !py-2 shadow-inner'>
								{isValidArray(category.group) &&
									category.group.map((tile, index) => (
										<Tile
											data={tile}
											key={index}
										/>
									))}
							</Collapse.Content>
						</Collapse>
					</li>
				))}

			{isValidArray(data.shop_all_cta) && (
				<li>
					<CTA
						data={data.shop_all_cta[0]}
						className='block bg-white p-4 text-sm font-semibold uppercase tracking-wider'
					/>
				</li>
			)}

			{data.category.find((cat) => cat.category_type === 'material') && (
				<li className='bg-white px-4 pt-3'>
					<List
						bullets='none'
						className='border-t-2 border-t-gray-5'
					>
						{data.category
							.filter((cat) => cat.category_type === 'material')
							?.map((materials, index) => (
								<li key={index}>
									<button
										onClick={onMenuItemClick}
										className='relative block w-full py-6 text-left text-sm font-semibold !leading-tight decoration-transparent'
										data-title={materials.heading}
										aria-label='default'
									>
										{materials.heading}
										<Icon
											name='keyboard_arrow_right'
											className='absolute right-4 top-1/2 -translate-y-1/2'
										/>
									</button>

									<List
										bullets='none'
										className='mobileMenu-tertiary invisible fixed -right-80 top-[59px] z-[11] h-[calc(100vh-59px)] w-full bg-white shadow-xl transition-all duration-500 sm:top-[63px] sm:h-[calc(100vh-63px)] sm:w-80'
										key={index}
									>
										<BackButton
											title={materials.heading}
											buttonText={parent}
											className='border-b border-b-gray-10'
										/>

										{materials.materials.map((material, index) => (
											<li
												key={index}
												className='px-4 py-3'
											>
												{isValidArray(material.cta_heading) && (
													<CTA
														data={material.cta_heading[0]}
														className='text-sm font-semibold no-underline'
													/>
												)}
												{isValidArray(material.entries) && (
													<p className='px-2 leading-tight'>
														{material.entries.map((link, index) => (
															<span
																className='after:content-[",_"] [&:last-child]:after:content-none'
																key={index}
															>
																<CTA
																	data={link}
																	className='text-xs text-gray-60 no-underline'
																/>
															</span>
														))}
													</p>
												)}
											</li>
										))}
									</List>
								</li>
							))}
					</List>
				</li>
			)}

			{isValidArray(data.category.filter((cat) => ['inspiration', 'resources'].includes(cat.category_type))) && (
				<li className='p-4'>
					<List bullets='none'>
						{data.category
							.filter((cat) => ['inspiration', 'resources'].includes(cat.category_type))
							.map((resource, index) => (
								<li key={index}>
									<button
										onClick={onMenuItemClick}
										className={`relative block w-full py-3 text-left text-sm !leading-tight decoration-transparent ${index === 0 ? 'border-b-2 border-b-gray-5' : ''}`}
										data-title={resource.heading}
										aria-label={resource.heading}
									>
										{resource.heading}
										<Icon
											name='keyboard_arrow_right'
											className='absolute right-0 top-1/2 -translate-y-1/2'
										/>
									</button>

									<List
										bullets='none'
										className='mobileMenu-tertiary invisible fixed -right-80 top-[59px] z-[11] h-[calc(100vh-59px)] w-full bg-white shadow-xl transition-all duration-500 sm:top-[63px] sm:h-[calc(100vh-63px)] sm:w-80'
										key={index}
									>
										<BackButton
											title={resource.heading}
											buttonText={parent}
											className='mb-1 border-b border-b-gray-10'
										/>

										{isValidArray(resource.entries) &&
											resource.entries.map((resource, index) => (
												<li key={index}>
													<CTA
														data={resource}
														className='inline-block px-4 py-3 text-sm font-semibold no-underline'
													/>
												</li>
											))}
									</List>
								</li>
							))}
					</List>
				</li>
			)}

			{data.category.find((cat) => cat.category_type === 'get started') && (
				<li className='bg-black p-4'>
					<p className='mb-2 text-lg leading-tight text-white'>{data.category.find((cat) => cat.category_type === 'get started').heading}</p>

					{isValidArray(data.category.find((cat) => cat.category_type === 'get started').entries) && (
						<List
							bullets='none'
							className='space-y-2'
						>
							{data.category
								.find((cat) => cat.category_type === 'get started')
								.entries.map((entry, index) => (
									<li key={index}>
										<CTA
											data={entry}
											className='flex items-center text-sm text-white no-underline [&>span]:mr-2.5'
											renderAs='link'
										/>
									</li>
								))}
						</List>
					)}
				</li>
			)}
		</List>
	);
};

ProductMenu.propTypes = {
	data: PropTypes.object.isRequired,
	onMenuItemClick: PropTypes.func,
	parent: PropTypes.string.isRequired,
	children: PropTypes.node,
};

export default ProductMenu;
